// library imports
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PageContent, PageHeader, PageWrapper, PageContainer } from 'components/common/layout_element/LayoutElement';
import * as Constants from "_core/configs/Constants";
import { FormatDateTime, GetEncodedCSV, DownloadCsvAsSJIS, DownloadCsvAsUtf8BOM } from "_core/utils/common";
import { useUpdateLock } from "_core/hooks/useUpdateLock";

// User Component imports
import dataManagerInstance from '_core/managers/DataManager';
import {Row, Button, TextInput, Stack, Card, Icon, Uploader, LoadingBox, Text, FileDownLoadButton, CommonModal} from "_core/components/common/UI";
import { InfoCard } from "components/common/parts";
import { setLoading, setMenuState, setErrorModal, setInitializeState } from "_core/slices/scene/SceneSlice";

// styles imports
import './AdminUserEdit.scss';
import * as SceneConfig from "_core/configs/SceneConfig";
import AdminUserEditApi from "apis/AdminUserEditApi";
import AdminUserDictionaryAssetUploadApi from "apis/AdminUserDictionaryAssetUploadApi";
import AdminDownloadTemplateApi from "apis/AdminDownloadTemplateApi";
import AdminDownloadFileApi from "apis/AdminDownloadFileApi";


const AdminUserEdit = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ExecAdminUserEditApi = AdminUserEditApi();
    const ExecAdminUserDictionaryAssetUploadApi = AdminUserDictionaryAssetUploadApi();
    const ExecAdminDownloadTemplateApi = AdminDownloadTemplateApi();
    const ExecAdminDownloadFileApi = AdminDownloadFileApi();

    const RequestType = {name: "1", mail: "2", password: "3", domain: "4"}
    const [userName, setUserName] = useState("");
    const [email, setEMail] = useState("");
    const [password, setPassword] = useState("");
    const [domain, setDomain] = useState("");
    const [domainList, setDomainList] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [prompt, setPrompt] = useState("");
    const [canUpdateName, setCanUpdateName] = useState(false);
    const [canUpdateEmail, setCanUpdateEmail] = useState(false);
    const [canUpdatePassword, setCanUpdatePassword] = useState(false);
    const { tUser, tUserAdd, tAccount, tAssetList } = useSelector((store) => store.transaction);
    // デモユーザーと読み取り専用権限は、どちらも更新系を使わせないためまとめて扱う
    const { isUpdateLocked } = useUpdateLock();
    const { initializeState } = useSelector((store) => store.scene);
    const [isFocus, setIsFocus] = useState(false);
    const [files, setFiles] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [dictionaryTAsset, setDictionaryTAsset] = useState(null);

    const [nameInputValid, setNameInputValid] = useState(false);
    const [emailInputValid, setEmailInputValid] = useState(false);
    const [passwordInputValid, setPasswordInputValid] = useState(false);

    const [ modalInfo , setModalInfo] = useState( {
        show: false,
        text: '',
        onClick: null,
    });
    
    const GetDomain = (targetDomain) => {
        if (!targetDomain) {
            return ""
        }

        if (targetDomain.includes("https://")) {
            const parsedUrl = new URL(targetDomain); // URLオブジェクトを生成
            return parsedUrl.hostname; // ホスト名を取得
        }
        else {
            return targetDomain;
        }
    }

    useEffect(() => {
        dispatch(setMenuState({ isHeader: false, isMenu: true, isFooter: false, isFullScreen: false }));
    }, [dispatch]);
    
    useEffect(() => {
        if(initializeState !== Constants.INITIALIZE_STATE.allComplete)
        {
            return
        }
        setUserName(tUser["username"])
        setEMail(tAccount["email"])
        setDomain(tUserAdd["domain"])
        setPrompt(tUserAdd["prompt"])
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete))
    }, [initializeState]);

    useEffect(() => {
        if (tUser == null)
        {
            return;
        }
        setCanUpdateName(userName && tUser["username"] != userName)
    }, [userName]);

    useEffect(() => {
        if (tAccount == null)
        {
            return;
        }
        setCanUpdateEmail(email && tAccount["email"] != email)
    }, [email]);

    useEffect(() => {
        if (tUser == null)
        {
            return;
        }
        setCanUpdatePassword(true)
    }, [password]);

    useEffect(() => {
        if (tUserAdd == null) {
            return;
        }
        if (domain === "") {
            return;
        }
        // ドメインをパースする
        const domains = domain
            .split(",")
            .map(item => item.trim())
            .filter(item => item !== "");
        setDomainList(domains);
    }, [domain]);

    useEffect(() => {
        console.log(tUserAdd);
        console.log(tAssetList);
        if (!tUserAdd || !tAssetList)
        {
            return
        }

        console.log(tAssetList.find(x => x.id === tUserAdd.dictionary_asset_id));

        // 辞書アセットの設定状況に応じて設定を更新
        setDictionaryTAsset(tUserAdd.dictionary_asset_id > 0 ? tAssetList.find(x => x.id === tUserAdd.dictionary_asset_id) : null);
    }, [tUserAdd, tAssetList]);

    const AdminUserEditApiRequest = async () => {

        // 通信中の表示を出す
        dispatch(setLoading(true));

        const requestParams = {
            t_user_id: tUser["id"]
        };

        if(password != "")
        {
            requestParams["password"] = password;
        }

        const result = await ExecAdminUserEditApi(requestParams);
        // 通信中の表示を消す
        dispatch(setLoading(false));
        if (result.error || (result.response && result.response.error_code > 0))
        {
            // エラーモーダル表示
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
        }
        else
        {
            setCanUpdateName(userName && result.response.t_user["username"] != userName)
            setCanUpdateEmail(email && result.response.t_account["email"] != email)
            if ("password" in requestParams)
            {
                setCanUpdatePassword(false)
            }
        }
    };

    const AdminDictionaryAssetsUploadApiRequest = async (file, file_name) => {
        // アセットアップロードAPI
        
        // 通信中の表示を出す
        dispatch(setLoading(true));

        const result = await ExecAdminUserDictionaryAssetUploadApi(file, file_name);
        // 通信中の表示を消す
        dispatch(setLoading(false));
        if (result.error || (result.response && result.response.error_code > 0))
        {
            // エラーモーダル表示
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            setFiles([]);
        }
        else
        {
            setFiles([]);
        }
    };

    const ExecDictionaryAssetsUploadAsync = () => {ExecDictionaryAssetsUpload()}
    const ExecDictionaryAssetsUpload = async () => {
        setListLoading(true);
        const page = await AdminDictionaryAssetsUploadApiRequest(files[0].file, files[0].file.name)

        setListLoading(false);
    }

    // テンプレートダウンロードAPI
    const AdminDownloadTemplateApiRequest = async () => {
        // 通信中の表示を出す
        dispatch(setLoading(true));

        const result = await ExecAdminDownloadTemplateApi({
            file_type: "DICTIONARY_FORMAT",
            fallbackFilename: "読み上げ辞書CSVテンプレート.csv",
        });

        // 通信中の表示を消す
        dispatch(setLoading(false));

        if (result.error || (result.response && result.response.error_code > 0)) {
            if (!result.response) {
                // エラーモーダル表示
                dispatch(setErrorModal({ show: true, text: {
                        "title": "エラー",
                        "message": "ダウンロードに失敗しました。"
                    }
                }));
            }
            else {
                // エラーモーダル表示
                dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            }
        }
    };

    // ファイルダウンロードAPI
    const AdminDownloadFileApiRequest = async (t_asset_id, t_asset_path) => {
        // 通信中の表示を出す
        dispatch(setLoading(true));

        const result = await ExecAdminDownloadFileApi({
            t_asset_id: t_asset_id,
            fallbackFilename: t_asset_path,
        });

        // 通信中の表示を消す
        dispatch(setLoading(false));

        if (result.error || (result.response && result.response.error_code > 0)) {
            if (!result.response) {
                // エラーモーダル表示
                dispatch(setErrorModal({ show: true, text: {
                        "title": "エラー",
                        "message": "ダウンロードに失敗しました。"
                    }
                }));
            }
            else {
                // エラーモーダル表示
                dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            }
        }
    };

    function ResetDummyPassword()
    {
        if (isFocus)
        {
            return;
        }
        setIsFocus(true)
        const passwordInput =  document.getElementById('password-input');
        passwordInput.value = ""
    };

    const GetCanUpdate = () => {
        if (isUpdateLocked)
        {
            return false;
        }

        console.log("canUpdateName: " + canUpdateName);
        console.log("canUpdateEmail: " + canUpdateEmail);
        console.log("canUpdatePassword: " + canUpdatePassword);

        console.log("nameInputValid: " + nameInputValid);
        console.log("emailInputValid: " + emailInputValid);
        console.log("passwordInputValid: " + passwordInputValid);


        // 更新可能な項目があるかどうかをチェック
        let canUpdate = (canUpdateName || canUpdateEmail || canUpdatePassword);

        // 変更予定のフォームの値が適切かチェック
        if (canUpdateName)
        {
            if (! nameInputValid)
            {
                canUpdate = false
            }
        }

        if (canUpdateEmail)
        {
            if (! emailInputValid)
            {
                canUpdate = false
            }
        }

        if (canUpdatePassword)
        {
            if (! passwordInputValid)
            {
                canUpdate = false
            }
        }

        return canUpdate;
    }

    return (
    <PageContent pageId="AdminUserEdit">
            <PageHeader>
                    <Stack className="spacing-4px">
                        <div className="page-header-title">基本情報</div>
                        <div className="page-header-detail">登録されている基本情報を更新します。</div>
                    </Stack>
            </PageHeader>
            <PageWrapper>
                <PageContainer>
                    {/* ここにページ内コンテンツのコンポーネントを記述 */}
                    <Stack className="block-center spacing-32px">
                        <Card className="base-card">
                            <Stack className="spacing-32px align-center">
                                <Row className= "input-row-area align-center spacing-16px">
                                    <Stack className="input-label-area stack flex-center">
                                        <div className="input-label input-label-text text-size-medium text-bold">会社名</div>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <TextInput 
                                            default={userName} 
                                            onchange={(e) => setUserName(e.target.value)} 
                                            placeholder="会社名を入力" 
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                            rules={[
                                                { type: "required"},
                                                { type: "minLength", value: 1},
                                                { type: "maxLength", value: 64},
                                            ]}
                                            disabled
                                        />
                                    </Stack>
                                </Row>
                                <hr/>
                                <Row className= "input-row-area align-center spacing-16px">
                                    <Stack className="input-label-area stack flex-center">
                                        <div className="input-label input-label-text text-size-medium text-bold">メールアドレス</div>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <TextInput 
                                            type="email" 
                                            default={email} 
                                            onchange={(e) => setEMail(e.target.value)} 
                                            placeholder="メールアドレスを入力"  
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                            rules={[
                                                { type: "required"},
                                                { type: "minLength", value: 1},
                                                { type: "maxLength", value: 64},
                                                { type: "custom", validate: ((v) => {return v.includes('@')}), message: "メールアドレスを入力してください。"},
                                            ]}
                                            disabled
                                        />
                                    </Stack>
                                </Row>
                                <hr/>
                                <Row className= "input-row-area align-center spacing-16px">
                                    <Stack className="input-label-area stack flex-center">
                                        <div className="input-label input-label-text text-size-medium text-bold">パスワード</div>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <TextInput 
                                            name="password-input"  
                                            type="password" 
                                            onfocus={() => ResetDummyPassword()} 
                                            onchange={(e) => setPassword(e.target.value)} 
                                            default="********" 
                                            firstClear={true}
                                            placeholder="パスワードを入力" 
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                            rules={[
                                                { type: "required"},
                                                { type: "minLength", value: 8},
                                                { type: "maxLength", value: 128},
                                            ]}
                                            onValidate={(valid) => {setPasswordInputValid(valid)}}
                                            disabled={isUpdateLocked}
                                        />
                                    </Stack>
                                </Row>
                                <hr/>
                                <Row className= "input-row-area align-center spacing-16px">
                                    <Stack className="input-label-area stack flex-center">
                                        <div className="input-label input-label-text text-size-medium text-bold">許可ドメイン</div>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center spacing-16px">
                                        <TextInput
                                            default={domainList.length > 0 ? GetDomain(domainList[0]) : ""}
                                            placeholder="許可ドメインを入力"
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                            disabled
                                        />
                                        {
                                            expanded && (
                                                domainList.slice(1).map((item, index) => (
                                                    <TextInput
                                                        key={"domain-" + index}
                                                        default={GetDomain(item)}
                                                        placeholder="許可ドメインを入力"
                                                        isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                                        disabled
                                                    />
                                                ))
                                            )
                                        }
                                        {
                                            domainList.length > 1 && (
                                                <Button
                                                    className="secondary-border expand-button"
                                                    onClick={() => setExpanded(!expanded)}
                                                >
                                                    {expanded ? "閉じる" : `さらに表示(${domainList.length - 1}件)`}
                                                </Button>
                                            )
                                        }
                                        <InfoCard>
                                            <div>PRizmoを設置するWEBページのドメイン名です。</div>
                                            <div>表示されているドメイン外からのアクセスはPRizmoの動作対象外です。</div>
                                        </InfoCard>
                                    </Stack>
                                </Row>
                            </Stack>
                            <Stack className="align-center margin-top-60px">
                                <Button
                                    className={"width-320px " + (GetCanUpdate() ? "primary": "primary disabled")}
                                    disabled={!GetCanUpdate()}
                                    onClick={() => AdminUserEditApiRequest()}
                                >
                                    更新
                                </Button>
                            </Stack>
                        </Card>
                        <Card className="base-card">
                            <Stack className="spacing-32px align-center">
                                <Row className= "flex-both align-center spacing-16px width-full">
                                    <Stack className="stack flex-center spacing-4px">
                                        <Text className="input-label input-label-text text-size-medium text-bold">読み上げ用辞書ファイル</Text>
                                        <Text className="input-label input-label-text text-size-small">{Constants.AI_NAME}が読み上げ時に使用する辞書ファイルを指定することができます。</Text>
                                        <Text className="input-label input-label-text text-size-small">辞書登録された単語は読み上げ時に変換されます。</Text>
                                    </Stack>
                                    <Stack>
                                        <div className="file-download-button">
                                            <Button
                                                className="padding-16px primary-border"
                                                onClick={() => AdminDownloadTemplateApiRequest()}
                                            >
                                                <Row className="flex-center spacing-16px">
                                                    <Icon icon="box-arrow-down" size={24} />
                                                    <div>テンプレートCSVダウンロード</div>
                                                </Row>
                                            </Button>
                                        </div>
                                    </Stack>
                                </Row>
                                <Stack className= "align-center spacing-16px width-full">
                                    {dictionaryTAsset &&
                                        <Card className="border-card file-display-area width-full">
                                            <Row className="flex-both align-center spacing-8px width-full">
                                                <Stack className="flex-center align-center">
                                                    {dictionaryTAsset && dictionaryTAsset.path}
                                                </Stack>
                                                <Row className="operate-area flex-right spacing-8px align-center">
                                                    <Row className="flex-center align-center spacing-8px width-full">
                                                        <Icon className="time-icon text-color-weak" icon="arrow-clockwise" size={24} />
                                                        <Text>{dictionaryTAsset && FormatDateTime(dictionaryTAsset.regdate)}</Text>
                                                    </Row>
                                                    <Button className={"operate-button secondary-border success"} onClick={() => {AdminDownloadFileApiRequest(dictionaryTAsset.id, dictionaryTAsset.path)}}><Row className="text-size-normal text-normal-weight operate-label width-fit">ダウンロード</Row></Button>
                                                </Row>
                                            </Row>
                                        </Card>
                                    }
                                    <Stack className="flex-center align-center spacing-16px width-full">
                                        <Uploader
                                            className="custom-uploader"
                                            accept={[".csv"]}
                                            onFileChanged={(files) => {
                                                console.log(files);
                                                setFiles(files)
                                            }}
                                            files={files}
                                            isSwap={true}
                                            minimum={false}
                                            disabled={isUpdateLocked}
                                        />
                                        {
                                            dictionaryTAsset &&
                                            <InfoCard className=" width-full input-form-area">
                                                    <div>ファイル更新時、古い辞書ファイルは削除されます。</div>
                                            </InfoCard>
                                        }
                                    </Stack>
                                    <Stack className="align-center">
                                        <Button 
                                            className={"width-320px " + (!isUpdateLocked && (files && files.length > 0) ? "primary" : "primary disabled")}
                                            disabled={isUpdateLocked || !(files && files.length > 0)}
                                            onClick={() => {
                                                if (dictionaryTAsset)
                                                {
                                                    setModalInfo({ show: true, title: "確認", text: "ファイルをアップロードすると古いファイルは削除されます。<br>更新してよろしいですか？", onclick: () => {
                                                        ExecDictionaryAssetsUploadAsync()
                                                    }});
                                                }
                                                else{
                                                    ExecDictionaryAssetsUploadAsync()
                                                }
                                            }}>
                                            {listLoading ? 
                                                <Stack className="flex-center align-center">
                                                    <LoadingBox></LoadingBox>
                                                </Stack>
                                                :
                                                <Row className="flex-center spacing-8px width-full">
                                                    <Icon className="label-icon" icon={dictionaryTAsset ? "arrow-clockwise" : "plus"} size={24} />
                                                    <div>{dictionaryTAsset ? "ファイルを更新" : "アップロード"}</div>
                                                </Row>
                                            }
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Card>
                    </Stack>
                </PageContainer>
            </PageWrapper>
            <CommonModal modalInfo={modalInfo} setModalInfo={setModalInfo}></CommonModal>
    </PageContent>
    );
};

export default AdminUserEdit;